import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Search, Cpu, ChevronDown, ArrowRight, Volume2, VolumeX, Box, Move3d } from "lucide-react";
import api, { inr, multiCurrencyLine } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

/* ------------------------------------------------------------------ *
 * ScrollBanner — cinematic 4-act 3D hero for Jeweltix.
 *   00 Intro  ->  01 Inspect  ->  02 Protect  ->  03 Buy
 *
 * A single pinned viewport drives:
 *   - a real STL model (Panther ring) loaded via STLLoader
 *   - a real circular-sprite sparkle field (canvas texture)
 *   - an additive halo bloom behind the ring
 *   - a metal-story arc (Silver -> Gold -> Rose Gold)
 *   - crossfading text stages + giant decorative numerals
 *   - a mouse-parallax offset (subtle)
 * All scroll-driven via a smoothed, lerped progress ref (60fps three.js).
 * ------------------------------------------------------------------ */

/**
 * Hero mesh config — full-quality GLB from the user's Panther STL.
 * 476,647 triangles preserved (no decimation), 8.2 MB (~3× smaller than
 * the raw 23 MB STL, cached after first paint).
 *   fit    : target longest-axis size (scene units)
 *   rotate : initial [x,y,z] Euler rotation (radians) applied after centering
 *   yOffset: nudge along Y after fit
 */
const HERO_STL = {
  url: "/hero/panther-ring.glb",
  fit: 2.6,
  // Stand upright (-90° X) + 75° roll around Z per user tweak
  rotate: [-Math.PI / 2, 0, (75 * Math.PI) / 180],
  yOffset: 0,
};

/* Build a soft radial sprite in-memory (transparent PNG w/o network). */
function makeSparkleTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.25, "rgba(191,227,255,0.85)");
  g.addColorStop(0.55, "rgba(125,211,252,0.35)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.needsUpdate = true;
  return t;
}

/* Palette for the metal-story arc. */
const METALS = [
  { p: 0.00, color: new THREE.Color(0xdde1e8) }, // Silver
  { p: 0.20, color: new THREE.Color(0xdde1e8) },
  { p: 0.45, color: new THREE.Color(0xffb400) }, // 22K Gold (warm, saturated)
  { p: 0.68, color: new THREE.Color(0xffb400) },
  { p: 1.00, color: new THREE.Color(0xff8a75) }, // Rose Gold (saturated warm pink)
];
function sampleMetal(p) {
  for (let i = 0; i < METALS.length - 1; i++) {
    const a = METALS[i], b = METALS[i + 1];
    if (p >= a.p && p <= b.p) {
      const u = (p - a.p) / (b.p - a.p || 1);
      return a.color.clone().lerp(b.color, u);
    }
  }
  return METALS[METALS.length - 1].color.clone();
}

/* Smoothstep + keyframe sampler for ring position/scale/tilt. */
const smoothstep = (t) => t * t * (3 - 2 * t);
const KEYFRAMES = [
  { p: 0.00, x:  1.75, y: -0.30, sc: 0.95, rx:  0.55, opacity: 1 },
  { p: 0.25, x:  1.90, y:  0.10, sc: 1.05, rx:  0.10, opacity: 1 },
  { p: 0.50, x:  0.00, y:  0.05, sc: 1.20, rx: -0.15, opacity: 1 },
  { p: 0.75, x: -1.90, y:  0.15, sc: 1.05, rx:  0.10, opacity: 1 },
  { p: 0.92, x:  0.00, y:  1.30, sc: 0.80, rx:  0.40, opacity: 1 },
  { p: 1.00, x:  0.00, y:  2.10, sc: 0.55, rx:  0.55, opacity: 0.15 },
];
function sampleKF(p) {
  for (let i = 0; i < KEYFRAMES.length - 1; i++) {
    const a = KEYFRAMES[i], b = KEYFRAMES[i + 1];
    if (p >= a.p && p <= b.p) {
      const u = smoothstep((p - a.p) / (b.p - a.p));
      return {
        x: a.x + (b.x - a.x) * u,
        y: a.y + (b.y - a.y) * u,
        sc: a.sc + (b.sc - a.sc) * u,
        rx: a.rx + (b.rx - a.rx) * u,
        opacity: a.opacity + (b.opacity - a.opacity) * u,
      };
    }
  }
  return KEYFRAMES[KEYFRAMES.length - 1];
}

export default function ScrollBanner() {
  const containerRef = useRef(null);
  const canvasMount = useRef(null);
  const scrollP = useRef(0);
  const lastScrollTs = useRef(0);
  const mouse = useRef({ x: 0, y: 0 });
  const userYaw = useRef(0);
  const userPitch = useRef(0);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const stageRef = useRef(0);
  const audioCtxRef = useRef(null);
  const mutedRef = useRef(true);
  const ringMeshRef = useRef(null);
  const [loadingStl, setLoadingStl] = useState(true);
  const [muted, setMuted] = useState(true);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [dragged, setDragged] = useState(false); // hide "drag" hint after first spin
  const nav = useNavigate();
  const { rates } = useCurrency();

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    api
      .get("/listings", { params: { featured: true } })
      .then((r) => setFeatured((r.data || []).slice(0, 8)))
      .catch(() => setFeatured([]));
  }, []);

  /* Lazily create AudioContext on first unmute (browsers require gesture). */
  const ensureAudio = () => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtxRef.current = new AC();
    return audioCtxRef.current;
  };

  /* A soft, bell-like chime (three sine partials, exponential decay). */
  const playChime = () => {
    if (mutedRef.current) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.14, now + 0.03);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 2.4);
      master.connect(ctx.destination);
      [880, 1320, 1760].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        const g = ctx.createGain();
        g.gain.value = [0.6, 0.28, 0.15][i];
        osc.connect(g).connect(master);
        osc.start(now);
        osc.stop(now + 2.5);
      });
    } catch (e) {
      // AudioContext may fail on Safari without a user gesture — ignore.
      if (process.env.NODE_ENV === "development") console.debug("chime failed:", e?.message);
    }
  };

  const toggleMute = () => {
    if (muted) {
      // Turning ON audio: ensure context now (user gesture)
      ensureAudio();
      // Play a tiny confirmation tick so the user hears something immediately
      mutedRef.current = false;
      playChime();
    }
    setMuted((m) => !m);
  };

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    scrollP.current = v;
    lastScrollTs.current = performance.now();
    // Stage detection: 0=intro, 1=inspect, 2=protect, 3=buy
    const s = v < 0.22 ? 0 : v < 0.52 ? 1 : v < 0.82 ? 2 : 3;
    if (s !== stageRef.current) {
      // Reward the reveal moment (entering "Inspect")
      if (s === 1 && stageRef.current === 0) playChime();
      stageRef.current = s;
    }
  });

  useEffect(() => {
    const mount = canvasMount.current;
    if (!mount) return;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const w = mount.clientWidth;
    const h = mount.clientHeight;

    /* ---------------- Scene / Camera / Renderer ---------------- */
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, w / h, 0.1, 100);
    camera.position.set(0, 0.4, 6.2);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    mount.appendChild(renderer.domElement);

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    /* ---------------- Lights (animated rim rig) ---------------- */
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 2.6);
    key.position.set(4, 6, 5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xffcf80, 1.8);
    rim.position.set(-5, -2, -3);
    scene.add(rim);
    const spot = new THREE.PointLight(0xffe0b0, 2.2, 20, 1.5);
    spot.position.set(0, 3, 3);
    scene.add(spot);

    /* ---------------- Ring group ---------------- */
    const ringGroup = new THREE.Group();
    scene.add(ringGroup);

    const bandMat = new THREE.MeshPhysicalMaterial({
      color: 0xdde1e8,
      metalness: 1,
      roughness: 0.34,
      clearcoat: 0.9,
      clearcoatRoughness: 0.18,
      envMapIntensity: 0.9,
    });

    /* Additive halo backlight — atmospheric glow behind the ring. */
    const haloTex = makeSparkleTexture();
    const haloMat = new THREE.SpriteMaterial({
      map: haloTex,
      color: 0xbfe3ff,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const halo = new THREE.Sprite(haloMat);
    halo.scale.setScalar(3.6);
    halo.position.set(0, 0, -0.4);
    ringGroup.add(halo);

    /* ---------------- Load real ring (full-quality GLB) ---------------- */
    // Guard: bail out cleanly if the component unmounts before the 8MB GLB
    // finishes downloading (prevents "Failed to fetch" console spam and any
    // state updates on an unmounted component).
    let alive = true;
    const gltfLoader = new GLTFLoader();
    gltfLoader.load(
      HERO_STL.url,
      (gltf) => {
        if (!alive) return;
        // Find the first mesh in the gltf scene and lift its geometry.
        let sourceGeom = null;
        gltf.scene.traverse((o) => {
          if (!sourceGeom && o.isMesh && o.geometry) sourceGeom = o.geometry;
        });
        if (!sourceGeom) {
          console.warn("Hero GLB contained no mesh geometry");
          setLoadingStl(false);
          return;
        }
        const geometry = sourceGeom;
        if (!geometry.attributes.normal) geometry.computeVertexNormals();
        geometry.center();
        geometry.computeBoundingBox();
        const size = new THREE.Vector3();
        geometry.boundingBox.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const s = HERO_STL.fit / maxDim;
        geometry.scale(s, s, s);
        const mesh = new THREE.Mesh(geometry, bandMat);
        mesh.rotation.set(
          HERO_STL.rotate[0],
          HERO_STL.rotate[1],
          HERO_STL.rotate[2]
        );
        mesh.position.y = HERO_STL.yOffset;
        ringGroup.add(mesh);
        ringMeshRef.current = mesh;

        setLoadingStl(false);

        setLoadingStl(false);
      },
      undefined,
      (err) => {
        if (!alive) return; // ignore aborted-on-unmount
        // Suppress the benign "Failed to fetch" abort that happens when the
        // 8MB GLB fetch is cancelled by a fast route change from Home.
        const msg = err && err.message ? err.message : String(err);
        if (/Failed to fetch|aborted/i.test(msg)) {
          setLoadingStl(false);
          return;
        }
        console.error("Hero GLB load failed:", err);
        setLoadingStl(false);
      }
    );

    ringGroup.rotation.x = 0.35;

    /* ---------------- Sparkle field (real circular sprites) ---------------- */
    const sparkTex = makeSparkleTexture();
    const sparkleCount = reducedMotion ? 60 : 260;
    const positions = new Float32Array(sparkleCount * 3);
    const speeds = new Float32Array(sparkleCount);
    const phases = new Float32Array(sparkleCount);
    for (let i = 0; i < sparkleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 24;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 14;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 16 - 4;
      speeds[i] = 0.2 + Math.random() * 0.9;
      phases[i] = Math.random() * Math.PI * 2;
    }
    const sparkleGeo = new THREE.BufferGeometry();
    sparkleGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const sparkleMat = new THREE.PointsMaterial({
      map: sparkTex,
      color: 0xbfe3ff,
      size: 0.14,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const sparkles = new THREE.Points(sparkleGeo, sparkleMat);
    scene.add(sparkles);

    /* Sparse gold glow layer */
    const goldPos = new Float32Array(70 * 3);
    for (let i = 0; i < 70; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 2.5 + Math.random() * 3;
      goldPos[i * 3] = Math.cos(a) * r;
      goldPos[i * 3 + 1] = (Math.random() - 0.5) * 5;
      goldPos[i * 3 + 2] = Math.sin(a) * r - 2;
    }
    const goldGeo = new THREE.BufferGeometry();
    goldGeo.setAttribute("position", new THREE.BufferAttribute(goldPos, 3));
    const goldMat = new THREE.PointsMaterial({
      map: sparkTex,
      color: 0xf0c65a,
      size: 0.22,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const goldGlow = new THREE.Points(goldGeo, goldMat);
    scene.add(goldGlow);

    /* ---------------- Mouse parallax + drag-to-rotate ---------------- */
    const onMouse = (e) => {
      const rect = mount.getBoundingClientRect();
      mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    };
    if (!reducedMotion) mount.addEventListener("mousemove", onMouse);

    // Drag-to-rotate — active when user isn't scrolling.
    // Small drags rotate the ring on Y (horizontal) and X (vertical) axes;
    // the added rotation persists on top of the scroll-driven auto-spin.
    const onPointerDown = (e) => {
      // Only respond to primary button / single-finger touch
      if (e.button !== undefined && e.button !== 0) return;
      dragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      mount.setPointerCapture?.(e.pointerId);
      mount.style.cursor = "grabbing";
      if (!dragged) setDragged(true);
    };
    const onPointerMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      dragStart.current = { x: e.clientX, y: e.clientY };
      userYaw.current += dx * 0.008;
      // Clamp pitch so the ring never flips upside-down beyond ±45°
      userPitch.current = Math.max(
        -Math.PI / 4,
        Math.min(Math.PI / 4, userPitch.current + dy * 0.006)
      );
    };
    const onPointerUp = (e) => {
      dragging.current = false;
      try { mount.releasePointerCapture?.(e.pointerId); } catch (_) { /* pointer was never captured — safe to ignore */ }
      mount.style.cursor = "";
    };
    mount.addEventListener("pointerdown", onPointerDown);
    mount.addEventListener("pointermove", onPointerMove);
    mount.addEventListener("pointerup", onPointerUp);
    mount.addEventListener("pointercancel", onPointerUp);
    mount.addEventListener("pointerleave", onPointerUp);

    /* ---------------- Animate ---------------- */
    const clock = new THREE.Clock();
    let raf;
    let pSmooth = 0;      // lerped scroll progress (damped)
    let mx = 0, my = 0;   // lerped mouse

    const animate = () => {
      raf = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      // Damp scroll for premium weight
      pSmooth += (scrollP.current - pSmooth) * 0.09;
      mx += (mouse.current.x - mx) * 0.06;
      my += (mouse.current.y - my) * 0.06;

      const kf = sampleKF(pSmooth);

      // Detect scroll-idle window — while idle, ease-off the automatic time-based
      // spin so the user's manual rotation feels solid (still keeps a lazy drift).
      const idle = performance.now() - lastScrollTs.current > 400;
      const timeSpin = (reducedMotion ? 0.05 : idle ? 0.05 : 0.22);

      // Ring transforms — scroll-driven yaw + user's drag yaw on top
      ringGroup.rotation.y =
        t * timeSpin + pSmooth * Math.PI * 2.3 + userYaw.current;
      ringGroup.rotation.x = kf.rx + my * 0.08 + userPitch.current;
      ringGroup.rotation.z = Math.sin(pSmooth * Math.PI * 2) * 0.10 + mx * 0.04;
      ringGroup.scale.setScalar(kf.sc);
      ringGroup.position.x = kf.x + mx * 0.15;
      ringGroup.position.y = kf.y + Math.sin(t * 0.6) * 0.04;

      // Fade ring on exit (visible during handoff to trust strip)
      if (ringMeshRef.current) {
        ringMeshRef.current.material.opacity = kf.opacity;
        ringMeshRef.current.material.transparent = kf.opacity < 1;
      }
      haloMat.opacity = 0.7 * kf.opacity;

      // Metal story-arc — Silver -> Gold -> Rose Gold
      const metal = sampleMetal(pSmooth);
      bandMat.color.copy(metal);

      // Camera choreography
      camera.position.z = 6.2 - pSmooth * 1.7;
      camera.position.y = 0.4 + Math.sin(t * 0.35) * 0.06;
      camera.position.x = mx * 0.25;
      camera.lookAt(0, 0.1, 0);

      // Sparkle drift + twinkle
      const sPos = sparkles.geometry.attributes.position.array;
      for (let i = 0; i < sparkleCount; i++) {
        sPos[i * 3 + 1] -= speeds[i] * 0.005;
        if (sPos[i * 3 + 1] < -7) sPos[i * 3 + 1] = 7;
      }
      sparkles.geometry.attributes.position.needsUpdate = true;
      sparkles.rotation.y = t * 0.03;
      sparkleMat.opacity = 0.6 + Math.sin(t * 1.4) * 0.25;

      goldGlow.rotation.y = -t * 0.05;
      goldMat.opacity = 0.5 + Math.sin(t * 0.9 + 1.2) * 0.25;

      // Light swell rakes across facets
      key.intensity = 2.6 + Math.sin(pSmooth * Math.PI * 4 + t * 0.5) * 0.9;
      halo.material.rotation = t * 0.3;

      renderer.render(scene, camera);
    };
    animate();

    /* ---------------- Resize ---------------- */
    const onResize = () => {
      const nw = mount.clientWidth;
      const nh = mount.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener("resize", onResize);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      mount.removeEventListener("mousemove", onMouse);
      mount.removeEventListener("pointerdown", onPointerDown);
      mount.removeEventListener("pointermove", onPointerMove);
      mount.removeEventListener("pointerup", onPointerUp);
      mount.removeEventListener("pointercancel", onPointerUp);
      mount.removeEventListener("pointerleave", onPointerUp);
      renderer.dispose();
      pmrem.dispose();
      sparkTex.dispose();
      haloTex.dispose();
      if (mount.contains(renderer.domElement))
        mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Text stage transforms ---------------- */
  const s1Op = useTransform(scrollYProgress, [0, 0.16, 0.22], [1, 1, 0]);
  const s1Y = useTransform(scrollYProgress, [0, 0.22], [0, -30]);

  const s2Op = useTransform(scrollYProgress, [0.22, 0.30, 0.45, 0.52], [0, 1, 1, 0]);
  const s2Y = useTransform(scrollYProgress, [0.22, 0.30], [30, 0]);

  const s3Op = useTransform(scrollYProgress, [0.52, 0.60, 0.75, 0.82], [0, 1, 1, 0]);
  const s3Y = useTransform(scrollYProgress, [0.52, 0.60], [30, 0]);

  const s4Op = useTransform(scrollYProgress, [0.82, 0.90, 1], [0, 1, 1]);
  const s4Y = useTransform(scrollYProgress, [0.82, 0.90], [30, 0]);

  // Giant decorative numerals (huge outlined type in bg)
  const n1Op = useTransform(scrollYProgress, [0, 0.15, 0.22], [0.11, 0.11, 0]);
  const n2Op = useTransform(scrollYProgress, [0.22, 0.30, 0.48, 0.52], [0, 0.11, 0.11, 0]);
  const n3Op = useTransform(scrollYProgress, [0.52, 0.60, 0.78, 0.82], [0, 0.11, 0.11, 0]);
  const n4Op = useTransform(scrollYProgress, [0.82, 0.90, 1], [0, 0.11, 0.11]);

  // Rail dot opacities (must be top-level hook calls, not in a loop)
  const dot1 = useTransform(scrollYProgress, [0, 0.05, 0.25, 0.30], [1, 1, 1, 0.3]);
  const dot2 = useTransform(scrollYProgress, [0.22, 0.30, 0.50, 0.55], [0.3, 1, 1, 0.3]);
  const dot3 = useTransform(scrollYProgress, [0.50, 0.55, 0.78, 0.82], [0.3, 1, 1, 0.3]);
  const dot4 = useTransform(scrollYProgress, [0.78, 0.85, 1], [0.3, 1, 1]);
  const chevronOp = useTransform(scrollYProgress, [0, 0.08, 0.12], [1, 1, 0]);

  const rail = [
    { label: "00 · Intro", op: dot1 },
    { label: "01 · Inspect", op: dot2 },
    { label: "02 · Protect", op: dot3 },
    { label: "03 · Buy", op: dot4 },
  ];

  return (
    <section
      ref={containerRef}
      className="relative bg-slate-950 text-white"
      style={{ height: "420vh" }}
      data-testid="scroll-banner"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Ambient layers */}
        <div className="absolute inset-0 mesh-grid opacity-25" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 45%, rgba(30,58,107,0.55) 0%, #0a1020 70%)",
          }}
        />

        {/* Giant decorative stage numerals */}
        <motion.div
          style={{ opacity: n1Op }}
          className="absolute inset-0 flex items-center justify-end px-4 lg:px-20 pointer-events-none select-none"
        >
          <span
            className="font-display font-black text-[38vh] leading-none tracking-tighter"
            style={{
              WebkitTextStroke: "1px rgba(125,211,252,0.55)",
              color: "transparent",
            }}
          >
            00
          </span>
        </motion.div>
        <motion.div
          style={{ opacity: n2Op }}
          className="absolute inset-0 flex items-center justify-start px-4 lg:px-20 pointer-events-none select-none"
        >
          <span
            className="font-display font-black text-[42vh] leading-none tracking-tighter"
            style={{
              WebkitTextStroke: "1px rgba(125,211,252,0.55)",
              color: "transparent",
            }}
          >
            01
          </span>
        </motion.div>
        <motion.div
          style={{ opacity: n3Op }}
          className="absolute inset-0 flex items-center justify-end px-4 lg:px-20 pointer-events-none select-none"
        >
          <span
            className="font-display font-black text-[42vh] leading-none tracking-tighter"
            style={{
              WebkitTextStroke: "1px rgba(240,198,90,0.55)",
              color: "transparent",
            }}
          >
            02
          </span>
        </motion.div>
        <motion.div
          style={{ opacity: n4Op }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
        >
          <span
            className="font-display font-black text-[46vh] leading-none tracking-tighter"
            style={{
              WebkitTextStroke: "1px rgba(220,169,160,0.55)",
              color: "transparent",
            }}
          >
            03
          </span>
        </motion.div>

        {/* Vignette on top of everything but text/canvas */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)",
          }}
        />

        {/* 3D canvas */}
        <div
          ref={canvasMount}
          className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none"
          data-testid="scroll-banner-canvas"
        />

        {/* Drag-to-rotate hint — fades out after first user drag */}
        {!dragged && (
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-32 lg:bottom-24 z-10 pointer-events-none font-mono text-[10px] uppercase tracking-[0.35em] text-primary/90 flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-black/40 backdrop-blur-sm"
            data-testid="drag-hint"
          >
            <Move3d className="h-3.5 w-3.5 animate-pulse" /> Drag to rotate
          </div>
        )}

        {/* Top-corner meta */}
        <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-primary/80 flex items-center gap-2 z-10">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          {loadingStl ? "Loading · Panther · STL" : "Live · Panther Ring · STL mesh"}
        </div>
        <div className="absolute top-6 right-6 hidden md:flex items-center gap-3 z-10">
          <button
            onClick={toggleMute}
            data-testid="scroll-banner-mute"
            aria-label={muted ? "Unmute banner chime" : "Mute banner chime"}
            title={muted ? "Enable scroll chime" : "Mute scroll chime"}
            className="pointer-events-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full border border-white/15 bg-black/30 text-slate-300 hover:border-primary hover:text-primary transition-colors backdrop-blur-sm"
          >
            {muted ? (
              <VolumeX className="h-3 w-3" />
            ) : (
              <Volume2 className="h-3 w-3 text-primary" />
            )}
            {muted ? "Sound off" : "Sound on"}
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
            Jeweltix / Cinematic Preview
          </span>
        </div>

        {/* STAGE 1 — Intro (text left, ring right) */}
        <motion.div
          style={{ opacity: s1Op, y: s1Y }}
          className="absolute inset-0 flex flex-col items-start justify-center px-8 lg:px-24 pointer-events-none z-10"
        >
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary border border-primary/30 rounded-full px-3 py-1 mb-6 backdrop-blur-sm bg-black/25">
              <Cpu className="h-3.5 w-3.5" /> Global · Jewellery CAD Marketplace
            </span>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight">
              The world's marketplace for{" "}
              <span className="text-primary">jewellery CAD files</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl">
              3DM &amp; STL files, production-ready. Verified designers across
              the globe, live technical preview, and escrow-protected payments.
            </p>
            <motion.div
              style={{ opacity: chevronOp }}
              className="mt-10 font-mono text-[10px] tracking-[0.35em] uppercase text-slate-400 flex items-center gap-2"
            >
              Scroll to inspect
              <ChevronDown className="h-4 w-4 animate-bounce" />
            </motion.div>
          </div>
        </motion.div>

        {/* STAGE 2 — Inspect (ring drifts right → center, text left) */}
        <motion.div
          style={{ opacity: s2Op, y: s2Y }}
          className="absolute inset-0 flex flex-col items-start justify-center px-8 lg:px-24 pointer-events-none z-10"
        >
          <div className="max-w-md">
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-primary mb-3 block">
              01 · Inspect
            </span>
            <h2 className="font-display font-bold text-3xl lg:text-5xl leading-tight">
              Every facet in real 3D.
            </h2>
            <p className="mt-4 text-slate-300 text-sm lg:text-base">
              Orbit, zoom, toggle wireframe or metal finish. Every listing
              renders the actual{" "}
              <span className="text-primary font-mono">.3dm</span> /{" "}
              <span className="text-primary font-mono">.stl</span> geometry —
              before you spend a cent.
            </p>
          </div>
        </motion.div>

        {/* STAGE 3 — Protect (ring drifts left, text right) */}
        <motion.div
          style={{ opacity: s3Op, y: s3Y }}
          className="absolute inset-0 flex flex-col items-end justify-center px-8 lg:px-24 pointer-events-none z-10"
        >
          <div className="max-w-md text-right">
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-primary mb-3 block">
              02 · Protect
            </span>
            <h2 className="font-display font-bold text-3xl lg:text-5xl leading-tight">
              Escrow. Every transaction.
            </h2>
            <p className="mt-4 text-slate-300 text-sm lg:text-base">
              Buyer funds are held for 48 hours. Raise a dispute and funds
              freeze instantly — refund, replace, or release, decided by our
              team.
            </p>
          </div>
        </motion.div>

        {/* STAGE 4 — Buy (ring rises out of frame; card centered lower half) */}
        <motion.div
          style={{ opacity: s4Op, y: s4Y }}
          className="absolute inset-0 flex flex-col items-center justify-end pb-24 lg:pb-28 px-6 pointer-events-none z-10"
        >
          {/* Featured reel — real listings floating behind the CTA */}
          {featured.length > 0 && (
            <div
              className="w-full max-w-[1300px] mb-8 lg:mb-10 relative pointer-events-none"
              style={{
                maskImage:
                  "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
              }}
              data-testid="scroll-banner-reel"
            >
              <motion.div
                className="flex gap-4 will-change-transform"
                animate={{ x: [0, -1400] }}
                transition={{ duration: 50, ease: "linear", repeat: Infinity }}
              >
                {[...featured, ...featured, ...featured].map((l, idx) => {
                  return (
                    <a
                      key={`${l.id}-${idx}`}
                      href={`/design/${l.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        nav(`/design/${l.id}`);
                      }}
                      className="pointer-events-auto shrink-0 w-40 lg:w-48 rounded-md overflow-hidden border border-white/15 bg-slate-900/80 backdrop-blur-sm hover:border-primary hover:-translate-y-1 transition-all group"
                      data-testid={`reel-item-${l.id}`}
                    >
                      <div className="aspect-square bg-slate-900 mesh-grid overflow-hidden flex items-center justify-center relative">
                        {l.cover_image ? (
                          <img
                            src={l.cover_image}
                            alt={l.title}
                            loading="lazy"
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                          />
                        ) : (
                          <Box
                            className="h-10 w-10 text-primary/70 group-hover:text-primary group-hover:scale-110 transition-transform"
                            strokeWidth={1.2}
                          />
                        )}
                        <span
                          className="absolute bottom-1.5 right-1.5 font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded text-white/90"
                          style={{
                            backgroundColor:
                              l.file_type === "3dm"
                                ? "rgba(240,198,90,0.9)"
                                : "rgba(59,130,246,0.9)",
                            color: "#0a1020",
                          }}
                        >
                          {l.file_type || "stl"}
                        </span>
                      </div>
                      <div className="p-2.5">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-primary/80 truncate">
                          {l.category_name || "Design"}
                        </p>
                        <p className="text-xs font-medium text-white truncate mt-0.5">
                          {l.title}
                        </p>
                        <p className="font-display font-bold text-sm text-white mt-1">
                          {inr(l.price)}
                        </p>
                        {rates && (
                          <p className="font-mono text-[9px] text-slate-400 mt-0.5 truncate">
                            {multiCurrencyLine(l.price, rates, ["USD", "EUR"])}
                          </p>
                        )}
                      </div>
                    </a>
                  );
                })}
              </motion.div>
            </div>
          )}

          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-primary mb-3">
            03 · Buy
          </span>
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-center max-w-2xl leading-tight">
            Find your next design.
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              nav(`/marketplace?q=${encodeURIComponent(q)}`);
            }}
            className="mt-6 flex items-center gap-2 bg-white rounded-md p-1.5 max-w-lg w-full pointer-events-auto shadow-2xl shadow-primary/25 ring-1 ring-primary/40"
          >
            <Search className="h-5 w-5 text-slate-400 ml-2" />
            <input
              data-testid="hero-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search rings, jhumkas, murti, mangalsutra…"
              className="flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none text-sm px-1"
            />
            <button
              className="bg-primary text-primary-foreground rounded px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="hero-search-btn"
            >
              Search
            </button>
          </form>
          <button
            data-testid="scroll-banner-explore"
            onClick={() => nav("/marketplace")}
            className="mt-5 pointer-events-auto font-mono text-[11px] uppercase tracking-[0.35em] text-slate-300 hover:text-primary transition-colors border-b border-transparent hover:border-primary pb-1 inline-flex items-center gap-2"
          >
            Or browse all 10,000+ designs <ArrowRight className="h-3 w-3" />
          </button>
        </motion.div>

        {/* Right-side stage rail */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-4 font-mono text-[10px] uppercase tracking-[0.3em] pointer-events-none z-10">
          {rail.map((r) => (
            <motion.div
              key={r.label}
              style={{ opacity: r.op }}
              className="flex items-center gap-3 text-slate-300"
            >
              <div className="h-px w-8 bg-current" />
              {r.label}
            </motion.div>
          ))}
        </div>

        {/* Bottom fade so it dissolves into the next section */}
        <div
          className="absolute bottom-0 inset-x-0 h-24 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(2,6,23,1))",
          }}
        />
      </div>
    </section>
  );
}
